import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AccountIdSchema } from '../models.accounts';
import {
  GenericConnectionId,
  GenericResourceId,
  GenericToolId,
  URLParamsSchema,
} from '../models.generic';
import { ResourceTypeSchema } from './integration.models.resource';

extendZodWithOpenApi(z);

const c = initContract();

export type ResourceQueryParams = z.infer<typeof ResourceQueryParamsSchema>;
export const ResourceQueryParamsSchema = z.object({
  accountId: AccountIdSchema,
  connectionId: GenericConnectionId.optional(),
  page: URLParamsSchema.page.default(1),
  limit: URLParamsSchema.limit.default(200),
  resourceType: ResourceTypeSchema.optional().describe(
    'Limit the results to a specific resource type'
  ),
  orderBy: z
    .enum(['title', 'created_at', 'updated_at'])
    .optional()
    .describe('The field to order the results by. Defaults to `updated_at`.'),
  orderDirection: z
    .enum(['asc', 'desc'])
    .optional()
    .describe('The direction to order the results by. Defaults to `desc`.'),
  search: z.string().optional().describe('A search term to filter the results by'),
  toolId: z.string().optional().describe('The ID of the tool to filter the results by'),
  ids: z.array(z.string()).optional().describe('Filter to specific resource IDs'),
  uris: z.array(z.string()).optional().describe('Filter to specific resource URIs'),
});

/**
 * Query params for connection-scoped "resource browsing" (admin/debug style).
 *
 * @remarks
 * This is intentionally separate from {@link ResourceQueryParamsSchema} because:
 * - `connectionId` is required (browse is connection-scoped)
 * - `accountId` is an optional *pin* used for validation (not the actual scope driver)
 * - browse supports hierarchical filters (parent/root)
 */
export type ResourceBrowseQueryParams = z.infer<typeof ResourceBrowseQueryParamsSchema>;
export const ResourceBrowseQueryParamsSchema = z.object({
  connectionId: GenericConnectionId.describe('Scope results to a single connection'),
  accountId: AccountIdSchema.optional().describe(
    'Optional pin to an account. If provided, must match the account of the connection (future: account-wide browsing).'
  ),
  resourceType: ResourceTypeSchema.optional().describe(
    'Limit the results to a specific resource type'
  ),
  parentId: GenericResourceId.optional().describe(
    'Limit results to direct children of this resource'
  ),
  rootId: GenericResourceId.optional().describe('Limit results to resources under this root'),
  toolId: GenericToolId.optional().describe('Limit results to a specific tool ID'),
  search: z.string().optional().describe('A search term to filter the results by'),
  page: URLParamsSchema.page.min(1).default(1).describe('Page number for pagination'),
  limit: URLParamsSchema.limit
    .min(1)
    .max(200)
    .default(50)
    .describe('Maximum number of results to return per page (1-200, default: 50)'),
  orderBy: z
    .enum(['title', 'created_at', 'updated_at'])
    .optional()
    .default('updated_at')
    .describe('The field to order the results by. Defaults to `updated_at`.'),
  orderDirection: z
    .enum(['asc', 'desc'])
    .optional()
    .default('desc')
    .describe('The direction to order the results by. Defaults to `desc`.'),
});
